import { useState } from 'react'
import { Item } from '@react-stately/collections'

import { Workspace } from 'types'
import { HeapEventType, trackEventInHeap } from 'utils/heapTracking'
import { LinkAngled } from 'blaze-ui/icons'
import { MailOpened } from 'blaze-ui/icons'
import InviteByEmail from './InviteByEmail'
import ImportEmails from './ImportEmails'
import Styles from './InviteTeam.module.scss'
import { Tabs } from 'components/BlazeTabs/Tabs'
import { Heading } from 'almanac-ui/Heading/Heading'
import { InviteByLink } from './InviteByLink'
import { useCurrentPlan } from 'hooks/useCurrentPlan'
import { ResponsiveDesign } from 'screens/blaze/Home/components/ResponsiveDesign'
import { MobileHeader } from './components/MobileHeader/MobileHeader'
import { Text } from 'blaze-ui/Text/Text'
import { WithAutopilotProPill } from 'components/AutopilotUpgradeButtons/AutopilotProPill'
import { useTierUsageLimits } from 'hooks/useTierUsageLimits'

export type Tab = 'email' | 'link'

interface InviteTeamProps {
  onClose: () => void
  workspace: Workspace
}

const InviteTeam = ({ onClose, workspace }: InviteTeamProps) => {
  const [showImport, setShowImport] = useState(false)
  const [currentTab, setCurrentTab] = useState<Tab>('email')
  const { data: currentPlan } = useCurrentPlan()

  const showImportScreen = () => {
    setShowImport(true)
    trackEventInHeap(HeapEventType.ImportContacts, { action: 'google_contact_import_started', type: '' })
  }
  const { atLimit, nextTierPlan, currentTierPlan } = useTierUsageLimits({
    limitKey: 'seats',
  })

  const shouldEnforceSeatLimit = atLimit && nextTierPlan && currentTierPlan

  return (
    <>
      <ResponsiveDesign.Mobile>
        <MobileHeader currentTab={currentTab} setCurrentTab={setCurrentTab} />
        <div className={Styles.mobileContentContainer}>
          {currentTab === 'email' && <InviteByEmail onClose={onClose} workspace={workspace} />}
          {currentTab === 'link' && showImport && (
            <ImportEmails onClose={onClose} setShowImport={setShowImport} workspace={workspace} />
          )}
          {currentTab === 'link' && !showImport && currentPlan && (
            <InviteByLink showImportScreen={showImportScreen} currentPlan={currentPlan} />
          )}
        </div>
      </ResponsiveDesign.Mobile>
      <ResponsiveDesign.Desktop>
        <div className={Styles.header}>
          <WithAutopilotProPill>
            <Heading level={3}>Invite Team Members</Heading>
          </WithAutopilotProPill>
        </div>
        <Tabs
          aria-label="Invite Options"
          classNames={{ tabItem: Styles.tabItem, tabs: Styles.tabs, tabContentsContainer: Styles.tabContentsContainer }}
          selectedKey={currentTab}
          animate
          onSelectionChange={(key) => setCurrentTab(key as Tab)}
          disabledKeys={workspace.is_ap_freemium || shouldEnforceSeatLimit ? ['link'] : []}
        >
          <Item
            key={'email'}
            title={
              <div className={Styles.tabContents}>
                <MailOpened />
                <Text variant="smallList">Invite by Email</Text>
              </div>
            }
          >
            {currentTab === 'email' && <InviteByEmail onClose={onClose} workspace={workspace} />}
          </Item>
          <Item
            key={'link'}
            title={
              <div className={Styles.tabContents}>
                <LinkAngled />
                <Text variant="smallList">Invite by Link</Text>
              </div>
            }
          >
            {currentTab === 'link' && showImport && (
              <ImportEmails onClose={onClose} setShowImport={setShowImport} workspace={workspace} />
            )}
            {currentTab === 'link' && !showImport && currentPlan && (
              <InviteByLink showImportScreen={showImportScreen} currentPlan={currentPlan} />
            )}
          </Item>
        </Tabs>
      </ResponsiveDesign.Desktop>
    </>
  )
}

export default InviteTeam
